import {AttrsSchema} from 'panel';
import {Component} from 'panel';

import {formatText} from 'common/util/format-text';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {RevealableTextEditor} from 'common/components/revealable-text-editor';
import {IronContextRegistry} from 'common/report/context-registry';

import css from './index.inline.styl';
// eslint-disable-next-line import/no-cycle
import {template} from './template';
import {ContextPanelData} from './types';

export type ContextPanelAttrs = {
  'tooltip-data'?: ContextPanelData;
  'project-is-free-plan'?: boolean;
  placement?: Directions;
  'force-placement'?: boolean;
  'position-strategy'?: PositioningStrategy;
  offset?: number;
  skidding?: number;
  'show-arrow'?: boolean;
  'always-expanded'?: boolean;
};

export type ContextPanelProps = {
  tooltipPositionParentElement?: HTMLElement;
  attrs?: ContextPanelAttrs;
};

type ContextPanelState = {
  description: string;
  expanded: boolean;
  openMoreDropdown: boolean;
};

export class ContextPanel extends Component<
  ContextPanelState,
  ContextPanelAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry
> {
  private _positionElement: HTMLElement = null;
  private textEditor: RevealableTextEditor;

  static get attrsSchema(): AttrsSchema<ContextPanelAttrs> {
    return {
      ...super.attrsSchema,
      'tooltip-data': `json`,
      'project-is-free-plan': `boolean`,
      placement: {
        type: `string`,
        default: Directions.Right,
        enum: Object.values(Directions),
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Absolute,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'force-placement': `boolean`,
      offset: {type: `number`, default: 2},
      skidding: {type: `number`, default: 0},
      'show-arrow': {type: `boolean`, description: `Whether or not to show a tooltip arrow`},
      'always-expanded': {
        type: `boolean`,
        description: `Remove the "Show more" button and always show all information`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`urlManager` as const],
      defaultState: {
        description: ``,
        expanded: false,
        openMoreDropdown: false,
      },
      helpers: {
        formatText,

        isTooltipDataSet: (): boolean => {
          const tooltipData = this.attr(`tooltip-data`);
          return !!tooltipData && Object.values(tooltipData).some((v) => (Array.isArray(v) ? !!v.length : !!v));
        },

        handleTextEditorSave: (ev, item) => {
          const description = ev.detail.value;
          this.dispatchEvent(
            new CustomEvent(`tooltipContextEditDescription`, {
              composed: true,
              cancelable: true,
              detail: {description, item},
            }),
          );
          this.update({description});
        },

        handleExpand: () => {
          this.update({expanded: true});
        },

        getActionSectionItems: () => {
          const {actions} = this.attr(`tooltip-data`);
          const actionItems = [];

          actions?.forEach((action) => {
            actionItems.push({label: action.label, value: action.name, icon: action.icon, item: action.item});
          });
          return [
            {
              items: actionItems,
            },
          ];
        },

        handleMouseEnter: () => {
          this.dispatchEvent(new CustomEvent(`hoverIn`));
        },

        handleActionClick: (actionItem) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: actionItem.value,
                item: actionItem.item,
              },
            }),
          );
        },

        handleMouseLeave: () => {
          this.dispatchEvent(new CustomEvent(`hoverOut`));
        },

        editorInserted: (vnode) => {
          this.textEditor = vnode.elm;
        },

        actionClick: (action) => {
          this.dispatchEvent(
            new CustomEvent(`tooltipContextAction`, {
              composed: true,
              cancelable: true,
              detail: {
                name: action.name,
                item: action.item,
              },
            }),
          );
        },

        linkClick: (link) => window.open(link.href, `_blank`),
      },
    };
  }

  public set tooltipPositionParentElement(el) {
    this._positionElement = el;

    if (this._positionElement !== el) {
      this.update();
    }
  }

  public get tooltipPositionParentElement() {
    return this._positionElement;
  }

  private setDescription() {
    if (this.attr(`tooltip-data`)) {
      const {description} = this.attr(`tooltip-data`);
      this.update({description});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.setDescription();
    this.closeMoreDropdown();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `tooltip-data`) {
      //TODO [shayan] find a better way
      if (this.textEditor) {
        this.textEditor.cancelEdit();
      }
      this.setDescription();
      this.closeMoreDropdown();
    }
  }

  openMoreDropdown() {
    this.update({openMoreDropdown: true});
  }

  closeMoreDropdown() {
    this.update({openMoreDropdown: false});
  }

  getUrlManager() {
    return this.getContext(`urlManager`);
  }
}

export const ContextPanelEl = defineAndMakeJsxFactory<ContextPanelProps>(`mp-context-panel`, ContextPanel);
export const ContextPanelReact = panel2react(`mp-context-panel`, ContextPanel);
